import { Certification } from '../../types/index';
import { format, parseISO } from 'date-fns';

interface CertificationsTableProps {
  certifications: Certification[];
}

export function CertificationsTable({ certifications }: CertificationsTableProps) {
  // Determine certification status based on completedDate and status field
  const getCertificationStatus = (cert: Certification): 'completed' | 'in-progress' | 'not-started' => {
    if (cert.completedDate && cert.completedDate.trim() !== '' && cert.status === 'active') {
      return 'completed';
    } else if (cert.completedDate && cert.completedDate.trim() !== '' && cert.status === 'pending') {
      return 'in-progress';
    } else {
      return 'not-started';
    }
  };

  const getStatusBadge = (cert: Certification) => {
    const certStatus = getCertificationStatus(cert);
    
    if (certStatus === 'completed') {
      return (
        <span className="bg-green-500 text-white px-2 py-0.5 rounded text-xs font-semibold uppercase border border-green-600">
          Completed
        </span>
      );
    } else if (certStatus === 'in-progress') {
      return (
        <span className="bg-tertiary text-white px-2 py-0.5 rounded text-xs font-semibold uppercase border" style={{ borderColor: '#4269a2' }}>
          In Progress
        </span>
      );
    } else {
      return (
        <span className="text-white px-2 py-0.5 rounded text-xs font-semibold uppercase border" style={{ backgroundColor: '#D44D5C', borderColor: '#D44D5C' }}>
          Not Started
        </span>
      );
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString || dateString.trim() === '') return 'Not Completed';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: 'rgba(var(--ion-color-secondary-rgb), 0.15)' }}>
        <h2 className="text-xl font-bold text-gray-800">Certifications</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Issuer
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Completed Date
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {certifications.map((cert) => (
              <tr key={cert.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {cert.name}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-700 max-w-md">
                    {cert.description || 'No description available'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-700">
                    {cert.issuer}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(cert)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-700">
                    {formatDate(cert.completedDate)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
